import React, { useCallback } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Section } from '../components/fx/AccentContext';
import { SectionLabel, TextLink } from '../components/home/ui';
import CareersHeader from '../components/careers/CareersHeader';
import CareersFooter from '../components/careers/CareersFooter';
import PositionDetail from '../components/careers/PositionDetail';
import { CPADX, FactRow, TypeBadge } from '../components/careers/ui';
import JsonLd from '../components/JsonLd';
import { positions } from '../data/careersJobs';
import { DEFAULT_TRACK } from '../data/careersContent';
import { POSTAL_CODE, companyProfile } from '../data/company';
import {
  DATE_POSTED,
  JOB_TITLE_JA,
  WORKSTYLE_HEADING,
  WORKSTYLE_NOTE,
  WORKSTYLE_ROWS,
  formatPostedDate,
} from '../data/jobPostings';
import { COMPANY, PAGE_META, jobMeta } from '../data/pageMeta';
import usePageMeta from '../lib/usePageMeta';
import { careersJobPath } from '../lib/routes';
import { jobPostingStructuredData } from '../lib/structuredData';

/**
 * 募集ごとのページ（/careers/jobs/<募集の id>）。2026-10-19。
 * Google の求人検索は、募集1つを書いたページにだけ求人の構造化データ（JobPosting）を置くよう求めるので、
 * 採用サイトの募集の一覧（/careers の募集要項）とは別に、募集ごとのページを作る。
 * 文言は data/careersJobs.js の募集のまま（採用サイトの募集要項と同じ）。足したのは、日本語の職種名と掲載日
 * （data/jobPostings.js）、勤務地（会社概要の所在地と郵便番号）だけ。
 * 採用サイトのヘッダーのナビは、/careers の各セクションへ移る。
 */
export default function CareersJob() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const job = positions.find((p) => p.id === jobId);
  const track = job?.track ?? DEFAULT_TRACK;

  usePageMeta(job ? jobMeta(job) : PAGE_META.careers);

  // 会社紹介資料はその募集の職種の資料へ、それ以外は /careers のそのセクションへ
  const goSection = useCallback((id) => navigate(`/careers/${id === 'deck' ? track : id}`), [navigate, track]);
  const goTrack = useCallback((key) => navigate(`/careers/${key}`), [navigate]);
  const goHome = useCallback(() => navigate('/careers'), [navigate]);

  if (!job) return <Navigate to="/careers/positions" replace />;

  const others = positions.filter((p) => p.id !== job.id);
  const address = companyProfile.find((p) => p.label === '所在地').value;

  return (
    <>
      <CareersHeader track={track} onNavigate={goSection} onTrack={goTrack} onHome={goHome} />

      <main className="bg-paper text-ink [&_h1]:[word-break:auto-phrase] [&_h2]:[word-break:auto-phrase]">
        <JsonLd data={jobPostingStructuredData(job)} />

        {/* ===== 募集要項 ===== */}
        <Section id="job" index={0} accent="green" className="border-b border-line">
          <div className={`${CPADX} pb-16 pt-28 md:pb-24 md:pt-36`}>
            <nav aria-label="パンくずリスト" className="text-[12px] text-gray-500">
              <ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <li>
                  <Link to="/careers" className="transition-colors hover:text-ink">
                    採用サイト
                  </Link>
                </li>
                <li aria-hidden="true">/</li>
                <li>
                  <Link to="/careers/positions" className="transition-colors hover:text-ink">
                    募集要項
                  </Link>
                </li>
                <li aria-hidden="true">/</li>
                <li aria-current="page" className="text-ink">
                  {job.title}
                </li>
              </ol>
            </nav>
            <SectionLabel className="mt-10">Open Position</SectionLabel>
            <PositionDetail
              job={job}
              titleJa={JOB_TITLE_JA[job.id]}
              as="article"
              headingAs="h1"
              bootcampTo="/careers/bootcamp"
              className="mt-8"
            />
          </div>
        </Section>

        {/* ===== 勤務地と働き方（全職種共通。採用サイトの「チームと働き方」と同じ文言） ===== */}
        <Section id="job-workstyle" index={1} accent="green" className="border-b border-line">
          <div className={`${CPADX} grid grid-cols-12 gap-y-8 py-16 md:py-20 lg:gap-x-12`}>
            <div className="col-span-12 lg:col-span-5">
              <SectionLabel>Workstyle</SectionLabel>
              <h2 className="mt-6 text-[22px] font-black leading-[1.5] tracking-display-ja text-ink md:text-[28px]">
                {WORKSTYLE_HEADING}
              </h2>
              <p className="mt-3 text-[13px] leading-[1.9] text-gray-600 md:text-[14px]">{WORKSTYLE_NOTE}</p>
            </div>
            <div className="col-span-12 min-w-0 lg:col-span-7">
              <div className="border-t border-ink">
                <FactRow label="会社">{COMPANY}</FactRow>
                <FactRow label="雇用形態">{job.type}</FactRow>
                <FactRow label="勤務地">{`〒${POSTAL_CODE} ${address}`}</FactRow>
                {WORKSTYLE_ROWS.map(([label, value]) => (
                  <FactRow key={label} label={label}>
                    {value}
                  </FactRow>
                ))}
                <FactRow label="掲載日">{formatPostedDate(DATE_POSTED[job.id])}</FactRow>
              </div>
            </div>
          </div>
        </Section>

        {/* ===== ほかの募集 ===== */}
        <Section id="other-positions" index={2} accent="green" className="border-b border-line">
          <div className={`${CPADX} py-16 md:py-20`}>
            <SectionLabel>Other Positions</SectionLabel>
            <h2 className="mt-6 text-[22px] font-black leading-[1.5] tracking-display-ja text-ink md:text-[28px]">
              ほかの募集
            </h2>
            <ul className="mt-8 border-t border-ink">
              {others.map((p) => (
                <li key={p.id} className="border-b border-line">
                  <Link
                    to={careersJobPath(p.id)}
                    data-cursor="VIEW"
                    className="group grid grid-cols-12 items-center gap-x-4 gap-y-1 py-5 md:py-6"
                  >
                    <span className="col-span-12 flex items-center gap-3 md:col-span-4">
                      <TypeBadge type={p.type} />
                      <span className="display-type text-[22px] text-ink transition-colors group-hover:text-live md:text-[26px]">
                        {p.title}
                      </span>
                    </span>
                    <span className="col-span-11 text-[13px] font-bold leading-[1.6] tracking-display-ja text-ink md:col-span-7 md:text-[14px]">
                      {p.catch}
                      <span className="mt-0.5 block text-[11.5px] font-medium text-gray-500">{p.jp}</span>
                    </span>
                    <span className="col-span-1 flex justify-end">
                      <ArrowRight
                        size={18}
                        className="text-ink transition-transform duration-300 group-hover:translate-x-1"
                        aria-hidden="true"
                      />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <TextLink to="/careers/positions" cursorLabel="FIND">
                あなたに合う仕事を見つける
              </TextLink>
            </div>
          </div>
        </Section>
      </main>

      <CareersFooter />
    </>
  );
}
